/**
 * DATA LAYER (DL): PERSONAL SURFER (ESQUIVADOR PERSONAL - HU-02/HU-03)
 * Módulo de comunicación HTTP (Fetch API) con la API REST de Interaktia-V2 para Esquivador Personal.
 * Gestiona endpoints de administración, overlay, telemetría y webapp móvil.
 * 
 * @module utilities/DL/PersonalSurfer
 */

export const getApiBaseUrl = () => {
    const rawApiUrl = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';
    const normalizedUrl = rawApiUrl.endsWith('/') ? rawApiUrl.slice(0, -1) : rawApiUrl;
    return `${normalizedUrl}/v1/personalsurfer`;
};

const sanitizeHeaderValue = (val) => {
    if (!val || typeof val !== 'string') return '';
    return val.replace(/[\r\n\t\0]/g, '').trim().substring(0, 100);
};

const buildHeaders = (roomId = null, customHeaders = {}) => {
    const headers = {
        'Content-Type': 'application/json',
        ...customHeaders
    };

    if (roomId) {
        const cleanRoomId = sanitizeHeaderValue(String(roomId));
        if (cleanRoomId) {
            headers['X-Room-Id'] = cleanRoomId;
        }
    }

    if (typeof localStorage !== 'undefined') {
        const token = localStorage.getItem('interaktia_token') || localStorage.getItem('token') || '';
        if (token) {
            headers['Authorization'] = `Bearer ${sanitizeHeaderValue(token)}`;
        }
    }

    return headers;
};

// =========================================================================
// ENDPOINTS DE ADMINISTRACIÓN Y OVERLAY
// =========================================================================

export const GetAdminData = async (roomId = null) => {
    try {
        const url = roomId ? `${getApiBaseUrl()}/admin-data?roomId=${encodeURIComponent(roomId)}` : `${getApiBaseUrl()}/admin-data`;
        const res = await fetch(url, {
            method: 'GET',
            headers: buildHeaders(roomId)
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        return { success: true, data };
    } catch (err) {
        console.error('[PersonalSurfer DL] Error en GetAdminData:', err);
        return { success: false, message: err.message };
    }
};

export const GetPublicConfig = async (roomId = null) => {
    try {
        const url = roomId ? `${getApiBaseUrl()}/config/${encodeURIComponent(roomId)}` : `${getApiBaseUrl()}/config`;
        const res = await fetch(url, {
            method: 'GET',
            headers: buildHeaders(roomId)
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        return { success: true, data };
    } catch (err) {
        console.error('[PersonalSurfer DL] Error en GetPublicConfig:', err);
        return { success: false, message: err.message };
    }
};

export const UpdateAdminConfig = async (configData, roomId = null) => {
    try {
        const res = await fetch(`${getApiBaseUrl()}/config`, {
            method: 'POST',
            headers: buildHeaders(roomId),
            body: JSON.stringify({ ...configData, roomId: roomId || configData.roomId })
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        return { success: true, data };
    } catch (err) {
        console.error('[PersonalSurfer DL] Error en UpdateAdminConfig:', err);
        return { success: false, message: err.message };
    }
};

export const UpdateAdminOverlay = async (overlayData, roomId = null) => {
    try {
        const res = await fetch(`${getApiBaseUrl()}/overlay`, {
            method: 'POST',
            headers: buildHeaders(roomId),
            body: JSON.stringify({ ...overlayData, roomId: roomId || overlayData.roomId })
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        return { success: true, data };
    } catch (err) {
        console.error('[PersonalSurfer DL] Error en UpdateAdminOverlay:', err);
        return { success: false, message: err.message };
    }
};

// =========================================================================
// GESTIÓN DE TEMAS VISUALES
// =========================================================================

export const GetThemes = async (roomId = null) => {
    try {
        const url = roomId ? `${getApiBaseUrl()}/themes?roomId=${encodeURIComponent(roomId)}` : `${getApiBaseUrl()}/themes`;
        const res = await fetch(url, {
            method: 'GET',
            headers: buildHeaders(roomId)
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        return { success: true, data };
    } catch (err) {
        console.error('[PersonalSurfer DL] Error en GetThemes:', err);
        return { success: false, message: err.message };
    }
};

export const SaveTheme = async (themeData, roomId = null) => {
    try {
        const isEdit = Boolean(themeData.id);
        const url = isEdit ? `${getApiBaseUrl()}/theme/${encodeURIComponent(themeData.id)}` : `${getApiBaseUrl()}/theme`;
        const method = isEdit ? 'PUT' : 'POST';

        const res = await fetch(url, {
            method,
            headers: buildHeaders(roomId),
            body: JSON.stringify({ ...themeData, roomId: roomId || themeData.roomId })
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        return { success: true, data };
    } catch (err) {
        console.error('[PersonalSurfer DL] Error en SaveTheme:', err);
        return { success: false, message: err.message };
    }
};

export const DeleteTheme = async (themeId, roomId = null) => {
    try {
        const url = `${getApiBaseUrl()}/theme/${encodeURIComponent(themeId)}${roomId ? `?roomId=${encodeURIComponent(roomId)}` : ''}`;
        const res = await fetch(url, {
            method: 'DELETE',
            headers: buildHeaders(roomId)
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        return { success: true, data };
    } catch (err) {
        console.error('[PersonalSurfer DL] Error en DeleteTheme:', err);
        return { success: false, message: err.message };
    }
};

export const ActivateTheme = async (themeId, roomId = null) => {
    try {
        const url = `${getApiBaseUrl()}/theme/${encodeURIComponent(themeId)}/activate${roomId ? `?roomId=${encodeURIComponent(roomId)}` : ''}`;
        const res = await fetch(url, {
            method: 'POST',
            headers: buildHeaders(roomId)
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        return { success: true, data };
    } catch (err) {
        console.error('[PersonalSurfer DL] Error en ActivateTheme:', err);
        return { success: false, message: err.message };
    }
};

// =========================================================================
// ENDPOINTS DE PARTICIPACIÓN MÓVIL Y LEADERBOARD
// =========================================================================

export const RegisterParticipant = async (participantData, roomId = null) => {
    try {
        const res = await fetch(`${getApiBaseUrl()}/register`, {
            method: 'POST',
            headers: buildHeaders(roomId),
            body: JSON.stringify({ ...participantData, roomId: roomId || participantData.roomId })
        });
        const data = await res.json();
        if (!res.ok) return { success: false, message: data.message || `HTTP ${res.status}` };
        return { success: true, data };
    } catch (err) {
        console.error('[PersonalSurfer DL] Error en RegisterParticipant:', err);
        return { success: false, message: err.message };
    }
};

export const SubmitScore = async (scoreData, roomId = null) => {
    try {
        const res = await fetch(`${getApiBaseUrl()}/score`, {
            method: 'POST',
            headers: buildHeaders(roomId),
            body: JSON.stringify(scoreData)
        });
        const data = await res.json();
        if (!res.ok) return { success: false, message: data.message || `HTTP ${res.status}` };
        return { success: true, data };
    } catch (err) {
        console.error('[PersonalSurfer DL] Error en SubmitScore:', err);
        return { success: false, message: err.message };
    }
};

export const DLTrackTelemetry = async (telemetryData) => {
    try {
        const res = await fetch(`${getApiBaseUrl()}/telemetry`, {
            method: 'POST',
            headers: buildHeaders(telemetryData.roomId),
            body: JSON.stringify(telemetryData)
        });
        return res.ok;
    } catch {
        return false;
    }
};

export const GetLeaderboard = async (roomId = null, top = 10) => {
    try {
        const url = `${getApiBaseUrl()}/leaderboard?top=${top}${roomId ? `&roomId=${encodeURIComponent(roomId)}` : ''}`;
        const res = await fetch(url, {
            method: 'GET',
            headers: buildHeaders(roomId)
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        return { success: true, data };
    } catch (err) {
        console.error('[PersonalSurfer DL] Error en GetLeaderboard:', err);
        return { success: false, message: err.message };
    }
};

// =========================================================================
// REINTENTOS, DANGER ZONE, UPLOADS Y EXCEL
// =========================================================================

export const ResetParticipant = async (participantId, roomId = null) => {
    try {
        const url = `${getApiBaseUrl()}/participant/${encodeURIComponent(participantId)}/reset${roomId ? `?roomId=${encodeURIComponent(roomId)}` : ''}`;
        const res = await fetch(url, {
            method: 'POST',
            headers: buildHeaders(roomId)
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        return { success: true, data };
    } catch (err) {
        console.error('[PersonalSurfer DL] Error en ResetParticipant:', err);
        return { success: false, message: err.message };
    }
};

export const FullReset = async (confirmationKeyword = 'REINICIAR', roomId = null) => {
    try {
        const res = await fetch(`${getApiBaseUrl()}/reset-all`, {
            method: 'POST',
            headers: buildHeaders(roomId),
            body: JSON.stringify({ confirmationKeyword, roomId })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);
        return { success: true, data };
    } catch (err) {
        console.error('[PersonalSurfer DL] Error en FullReset:', err);
        return { success: false, message: err.message };
    }
};

export const UploadAsset = async (file, assetType, roomId = null) => {
    try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('assetType', assetType);
        if (roomId) formData.append('roomId', roomId);

        const headers = {};
        if (roomId) headers['X-Room-Id'] = sanitizeHeaderValue(String(roomId));
        if (typeof localStorage !== 'undefined') {
            const token = localStorage.getItem('interaktia_token') || localStorage.getItem('token') || '';
            if (token) headers['Authorization'] = `Bearer ${sanitizeHeaderValue(token)}`;
        }

        const res = await fetch(`${getApiBaseUrl()}/upload`, {
            method: 'POST',
            headers,
            body: formData
        });

        const data = await res.json();
        if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);
        return { success: true, data };
    } catch (err) {
        console.error('[PersonalSurfer DL] Error en UploadAsset:', err);
        return { success: false, message: err.message };
    }
};

export const getExportExcelUrl = (roomId = null) => {
    return `${getApiBaseUrl()}/export-excel${roomId ? `?roomId=${encodeURIComponent(roomId)}` : ''}`;
};
